import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Timer, TrendingUp, TrendingDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { ReadingSpeedData } from '@/lib/analyticsUtils';

interface ReadingSpeedCardProps {
  speedData: ReadingSpeedData;
}

export function ReadingSpeedCard({ speedData }: ReadingSpeedCardProps) {
  const { averagePagesPerDay, totalReadingDays, fastestBook, slowestBook } = speedData;

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Timer className="w-5 h-5 text-primary" />
          Reading Speed
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-3xl font-bold text-primary mb-1">
              {averagePagesPerDay}
            </div>
            <p className="text-sm text-muted-foreground">Pages per Day</p>
          </div>
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-3xl font-bold text-secondary mb-1">
              {totalReadingDays}
            </div>
            <p className="text-sm text-muted-foreground">Total Reading Days</p>
          </div>
        </div>

        {/* Fastest Book */}
        {fastestBook && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <TrendingUp className="w-4 h-4 text-primary" />
              Fastest Read
            </div>
            <div className="pl-6 space-y-1">
              <p className="font-medium">{fastestBook.title}</p>
              <p className="text-sm text-muted-foreground">
                {fastestBook.authors.join(', ')}
              </p>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {fastestBook.pagesPerDay} pages/day
                </Badge>
                <span className="text-xs text-muted-foreground">
                  in {fastestBook.totalDays} day{fastestBook.totalDays !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Slowest Book */}
        {slowestBook && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <TrendingDown className="w-4 h-4 text-accent" />
              Most Savored
            </div>
            <div className="pl-6 space-y-1">
              <p className="font-medium">{slowestBook.title}</p>
              <p className="text-sm text-muted-foreground">
                {slowestBook.authors.join(', ')}
              </p>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {slowestBook.pagesPerDay} pages/day
                </Badge>
                <span className="text-xs text-muted-foreground">
                  over {slowestBook.totalDays} day{slowestBook.totalDays !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
